import * as Sentry from "@sentry/browser";
import { browserTracingIntegration } from "@sentry/browser";

const sentryExceptionDenyList = [
    // Browser/network noise from user-side connectivity issues.
    /TypeError: Failed to fetch/i,
    // Axios wrapper for transient user/network failures.
    /AxiosError: Network Error/i,
    // Safari variant of fetch failures.
    /TypeError: Load failed/i,
    // Browser extension conflicts mutating globals.
    /TypeError: Cannot redefine property: create/i,
    // useFetch (vueuse) wraps non-JSON server responses (e.g. HTML 500 pages) as
    // NonJsonResponseError. These are backend failures already tracked server-side
    // and are not actionable on the frontend. See vue/api/index.ts onFetchError.
    /NonJsonResponseError/i,
    // vueuse/core occasionally throws this with no actionable context.
    /Error: No error message/i,
    // Third-party iframe embedding edge-case outside our control.
    /Error: Initialization Failed\. No Parent window found/i,
    // useFetch (vueuse) abort signals fired on navigation or component unmount.
    /signal is aborted without reason/i,
];

const shouldDropSentryEvent = (event: Sentry.ErrorEvent): boolean => {
    const exceptionValues = event?.exception?.values ?? [];

    // Drop opaque unknown exceptions that don't include any usable stack frames.
    const hasUnknownExceptionWithoutStack = exceptionValues.some((exception) => {
        const typeOrValue = [exception?.type, exception?.value].filter(Boolean).join(": ");
        const hasUnknownTypeOrValue = /<unknown>/i.test(typeOrValue);
        const hasStackFrames = Boolean(exception?.stacktrace?.frames?.length);

        return hasUnknownTypeOrValue && !hasStackFrames;
    });

    if (hasUnknownExceptionWithoutStack) {
        return true;
    }

    return exceptionValues.some((exception) => {
        const typeAndValue = [exception?.type, exception?.value].filter(Boolean).join(": ");
        return sentryExceptionDenyList.some((pattern) => pattern.test(typeAndValue));
    });
};

const config = window.__SENTRY_CONFIG__;

if (config?.dsn && config?.environment) {
    Sentry.init({
        dsn: config.dsn,
        environment: config.environment,
        release: config.release,
        integrations: [browserTracingIntegration()],
        beforeSend(event) {
            if (shouldDropSentryEvent(event)) {
                return null;
            }
            return event;
        },
        ignoreErrors: [
            // Browser "Failed to fetch" errors are unactionable noise caused by
            // users navigating away, network drops, or corporate firewalls.
            "Failed to fetch",
            // WebSocket connection errors are non-actionable user-side connectivity
            // blips (tab switches, Wi-Fi drops, laptop closes). ~25k events/day.
            "Could not connect to websocket",
            "WebSocket is closed",
            /Failed to send socket message after \d+ retries/,
            "Still in CONNECTING state",
            "ConnectionClosedOK",
        ],
    });

    if (config.userId) {
        Sentry.setTag("user_id", config.userId);
    }
    if (config.organizationName) {
        Sentry.setTag("organization", config.organizationName);
    }
}

export default Sentry;
